import { BarChart, Card, CardTitle, EmptyState } from '@wundo/ui';
import type { LlmLatencyByModel } from '@/lib/usageCost.ts';

/** How fast each model answered: generation speed and time to the first token. */
export function LatencyCard({ latency }: { latency: LlmLatencyByModel[] }) {
  const decodeModels = latency.filter((model) => model.decode_samples > 0);
  const ttftModels = latency.filter((model) => model.ttft_samples > 0);

  return (
    <Card as='section' className='flex flex-col gap-3.5'>
      <div className='flex flex-wrap items-baseline justify-between gap-2'>
        <CardTitle>LLM performance by model</CardTitle>
        <span className='text-xs text-ink-muted'>
          sync calls · taller decode = faster, lower TTFT = better
        </span>
      </div>

      {latency.length === 0 ? (
        <EmptyState title='No data' headingLevel='h3' />
      ) : (
        <div className='grid grid-cols-1 gap-5 agenda:grid-cols-2'>
          <div className='flex flex-col gap-2'>
            <p className='text-xs font-semibold text-ink-secondary'>
              Generation speed (tokens / sec)
            </p>
            {decodeModels.length > 0 ? (
              <BarChart
                ariaLabel='Generation speed per model, tokens per second'
                labels={decodeModels.map((model) => model.model)}
                series={[
                  {
                    label: 'Median',
                    data: decodeModels.map((model) => model.decode_p50 ?? 0),
                  },
                  {
                    label: 'Slow (p5)',
                    data: decodeModels.map((model) => model.decode_p05 ?? 0),
                  },
                ]}
                yFormat={(value) => `${value} tok/s`}
                height={224}
              />
            ) : (
              <p className='text-xs text-ink-muted'>
                No decode-speed data in this window.
              </p>
            )}
          </div>

          <div className='flex flex-col gap-2'>
            <p className='text-xs font-semibold text-ink-secondary'>
              Time to first token (ms)
            </p>
            <BarChart
              ariaLabel='Time to first token per model, milliseconds'
              labels={ttftModels.map((model) => model.model)}
              series={[
                {
                  label: 'Median',
                  data: ttftModels.map((model) => model.ttft_p50 ?? 0),
                },
                {
                  label: 'Slow (p95)',
                  data: ttftModels.map((model) => model.ttft_p95 ?? 0),
                },
              ]}
              yFormat={(value) => `${value} ms`}
              height={224}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
